import type { ReactElement, ReactNode } from "react";
import React, { useMemo } from "react";

import cn from "classnames";

import {
    BADGE_SIZE_BY_AVATAR_SIZE_MAP,
    COUNTER_SIZE_BY_AVATAR_SIZE_MAP,
    GIFT_SIZE_BY_AVATAR_SIZE_MAP,
    ICON_SIZE_BY_AVATAR_SIZE_MAP,
    ONLINE_INDICATOR_SIZE_BY_AVATAR_SIZE_MAP,
    TAG_SIZE_BY_AVATAR_SIZE_MAP,
} from "./constants/sizes";
import { formatCount } from "./utils";
import { extractProps, getChildrenRecord } from "../../../utils/react";
import { Icon } from "../../Icon";

import type { AvatarProps, AvatarSize } from "./types";
import type { IconProps } from "../../Icon";

import classes from "./Avatar.styl";

export const AVATAR_TEST_ID = "avatar-test-id";
const AVATAR_DISPLAY_NAME = "Avatar";
const INITIALS_MAX_LENGTH = 2;
const GIFT_CONSUMERS_CONTENTS = ["image", "placeholder"];

/**
 * Компонент для отображения аватарки группы или пользователя. Может содержать изображение, плейсхолдер, иконку или текст.
 * Дополнительно может отображать различные индикаторы в виде компонентов online, badge и counter
 *
 * <a href="https://www.figma.com/design/vTg3KAvmAXA9LigeyiqCWL/%F0%9F%92%A0-Base-Components?m=auto&node-id=2-89512&t=xOS5YJTI37Iwp4AM-1" target="_blank">Спека</a>
 * @constructor
 *
 * @deprecated Используйте компонент AvaPicture.
 */
export const Avatar = <S extends AvatarSize>({
    size,
    content = "image",
    stroke,
    ring,
    overlay,
    view = "primary",
    children,
    "data-tsid": tsid,
    ...otherProps
}: AvatarProps<S>): ReactElement => {
    const {
        /** Пропсы для content="image" */
        src,
        alt,
        /** Пропсы для content="initials" */
        initials,
        /** Пропсы для content="count" */
        count,
        /** Пропсы для content="icon" */
        icon,
        customColorsStyles,
    } = extractProps(otherProps, [
        "src",
        "alt",
        "initials",
        "count",
        "icon",
        "customColorsStyles",
    ]);

    const { OnlineIndicator, Counter, Badge, Gift, Tag, Stub } = useMemo(() => {
        const badgeSize =
            BADGE_SIZE_BY_AVATAR_SIZE_MAP[
                size as keyof typeof BADGE_SIZE_BY_AVATAR_SIZE_MAP
            ];
        const onlineIndicatorSize =
            ONLINE_INDICATOR_SIZE_BY_AVATAR_SIZE_MAP[
                size as keyof typeof ONLINE_INDICATOR_SIZE_BY_AVATAR_SIZE_MAP
            ];
        const counterSize =
            COUNTER_SIZE_BY_AVATAR_SIZE_MAP[
                size as keyof typeof COUNTER_SIZE_BY_AVATAR_SIZE_MAP
            ];
        const giftSize =
            GIFT_SIZE_BY_AVATAR_SIZE_MAP[
                size as keyof typeof GIFT_SIZE_BY_AVATAR_SIZE_MAP
            ];
        const tagSize =
            TAG_SIZE_BY_AVATAR_SIZE_MAP[
                size as keyof typeof TAG_SIZE_BY_AVATAR_SIZE_MAP
            ];

        return getChildrenRecord(children, "Avatar", {
            OnlineIndicator: {
                props: {
                    size: onlineIndicatorSize,
                },
                skipElement: !onlineIndicatorSize,
            },
            Counter: {
                props: {
                    size: counterSize,
                },
                skipElement: !counterSize,
            },
            Badge: {
                props: {
                    size: badgeSize,
                },
                skipElement: !badgeSize,
            },
            Gift: {
                props: {
                    width: giftSize,
                    height: giftSize,
                },
                skipElement:
                    !giftSize || !GIFT_CONSUMERS_CONTENTS.includes(content),
            },
            Tag: {
                props: {
                    size: tagSize,
                },
                skipElement: !tagSize,
            },
            Unknown: {
                skipElement: true,
            },
        });
    }, [children, content, size]);

    const contentElement = useMemo<ReactNode>(() => {
        const iconProps = {
            icon,
            size: ICON_SIZE_BY_AVATAR_SIZE_MAP[size],
        } as IconProps;

        const renderIcon = () => <Icon {...iconProps} />;

        switch (content) {
            case "image":
                return (
                    <>
                        {src && (
                            <img
                                src={src}
                                alt={alt}
                                className={classes["image"]}
                            />
                        )}
                        {overlay && (
                            <div className={classes["overlay-icon"]}>
                                {renderIcon()}
                            </div>
                        )}
                    </>
                );
            case "initials":
                return initials!.length > INITIALS_MAX_LENGTH
                    ? initials!.substring(0, INITIALS_MAX_LENGTH)
                    : initials;
            case "count":
                return formatCount(count!);
            case "icon":
                return renderIcon();
        }
    }, [icon, size, content, src, alt, overlay, initials, count]);

    const wrapperClasses = cn(
        classes["wrapper"],
        content === "image" && classes["__inner_stroke"],
        content !== "image" && classes[`__content-${content}`],
        classes[`__size-${size}`],
        classes[`__view-${view}`],
        {
            [classes["__stroke"]]: stroke,
            [classes["__ring"]]: ring && !Tag,
            [classes["__overlay"]]: overlay,
        },
        customColorsStyles,
    );

    return (
        <div
            className={wrapperClasses}
            data-tsid={tsid || AVATAR_TEST_ID}
            data-uikit={AVATAR_DISPLAY_NAME}
        >
            <div className={classes["content-wrapper"]}>
                {Stub}
                {contentElement}
            </div>
            {!Tag && (
                <>
                    {OnlineIndicator && (
                        <div className={classes["online-indicator-wrapper"]}>
                            {OnlineIndicator}
                        </div>
                    )}
                    {Counter && (
                        <div className={classes["counter-wrapper"]}>
                            {Counter}
                        </div>
                    )}
                    {Badge && (
                        <div className={classes["badge-wrapper"]}>{Badge}</div>
                    )}
                    {Gift && (
                        <div className={classes["gift-wrapper"]}>{Gift}</div>
                    )}
                </>
            )}
            {Tag && <div className={classes["tag-wrapper"]}>{Tag}</div>}
        </div>
    );
};

Avatar.displayName = AVATAR_DISPLAY_NAME;

export default Avatar;
